import React, { useCallback, useEffect, useMemo, useState } from 'react';
import platformApi, { platformErrorMessage } from '../services/platformApi';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import EmptyState from '../components/ui/EmptyState';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import {
  ACTION_LABELS,
  autopayEndReasonLabel,
  autopayMethodLabel,
  autopayStatus,
  availableActions,
  buildLifecycleRequest,
  formatDate,
  formatInr,
  isFreePlan,
  payerLabel,
  subscriptionStatusLabel,
  toDateInput,
  trialNeedsPlan,
} from '../utils/billingAdmin';

const STATUSES = ['trial', 'active', 'grace', 'suspended', 'cancelled', 'expired'];
const DESTRUCTIVE = new Set(['cancelNow', 'suspend', 'cancelAtPeriodEnd']);

function tenantLabel(sub) {
  return sub.tenant?.name || sub.tenant?.subdomain || sub.tenantId || '—';
}

function periodLabel(sub) {
  if (sub.status === 'trial') return `Trial ends ${formatDate(sub.trialEnd)}`;
  if (isFreePlan(sub)) return 'No end date';
  if (!sub.subscriptionEnd) return 'No end date';
  return `Paid until ${formatDate(sub.subscriptionEnd)}`;
}

function planPrice(plan, cycle) {
  if (!plan) return '';
  if (cycle === 'yearly') return `${formatInr(plan.priceYearly, { decimals: false })}/yr`;
  if (cycle === 'monthly') return `${formatInr(plan.priceMonthly, { decimals: false })}/mo`;
  return '';
}

function ActionForm({ action, subscription, plans, busy, onSubmit, onCancel }) {
  const needsPlan = action === 'activate' || action === 'changePlan' || (action === 'extendTrial' && trialNeedsPlan(subscription));
  const [form, setForm] = useState(() => ({
    planId: action === 'changePlan' || action === 'activate' ? subscription.planId || '' : '',
    billingCycle: subscription.billingCycle && subscription.billingCycle !== 'none' ? subscription.billingCycle : 'monthly',
    subscriptionEnd: toDateInput(subscription.subscriptionEnd),
    days: 7,
    reason: '',
  }));
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const selectablePlans = action === 'extendTrial'
    ? plans.filter((p) => p.code !== 'free' && p.status === 'active')
    : plans.filter((p) => p.status === 'active' || p.id === subscription.planId);

  const submit = (e) => {
    e.preventDefault();
    const request = buildLifecycleRequest(action, subscription, form);
    if (request.error) {
      setError(request.error);
      return;
    }
    setError(null);
    onSubmit(request);
  };

  return (
    <form onSubmit={submit} className="card card__body" style={{ marginTop: '1rem' }}>
      <h3 style={{ marginTop: 0 }}>{ACTION_LABELS[action]}</h3>
      {needsPlan && (
        <div className="form-group">
          <label className="form-label" htmlFor="action-plan">Plan</label>
          <select id="action-plan" className="form-select" value={form.planId} onChange={set('planId')}>
            <option value="">Choose a plan</option>
            {selectablePlans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}{plan.isPublic === false ? ' (private)' : ''}{plan.status !== 'active' ? ` (${plan.status})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      {(action === 'activate' || action === 'changePlan') && (
        <>
          <div className="form-group">
            <label className="form-label" htmlFor="action-cycle">Billing cycle</label>
            <select id="action-cycle" className="form-select" value={form.billingCycle} onChange={set('billingCycle')}>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
              <option value="none">No end date (complimentary)</option>
            </select>
          </div>
          {form.billingCycle !== 'none' && (
            <div className="form-group">
              <label className="form-label" htmlFor="action-end">Paid until (end of day, IST)</label>
              <input id="action-end" type="date" className="form-input" value={form.subscriptionEnd} onChange={set('subscriptionEnd')} />
            </div>
          )}
          <p className="form-hint">
            This changes access only. No payment is taken and no invoice is issued.
          </p>
        </>
      )}
      {action === 'extendTrial' && (
        <div className="form-group">
          <label className="form-label" htmlFor="action-days">
            {subscription.status === 'trial' ? 'Add days to the trial' : 'Trial length (days)'}
          </label>
          <input id="action-days" type="number" min="1" max="365" className="form-input" value={form.days} onChange={set('days')} />
        </div>
      )}
      {action === 'cancelNow' && (
        <p className="form-hint">Access to paid features stops now and the workspace falls back to Free. No refund is made automatically.</p>
      )}
      {action === 'cancelAtPeriodEnd' && (
        <p className="form-hint">The plan stays active until {formatDate(subscription.subscriptionEnd)}, then moves to Free.</p>
      )}
      {action === 'suspend' && (
        <p className="form-hint">The workspace loses access until it is reactivated.</p>
      )}
      <div className="form-group">
        <label className="form-label" htmlFor="action-reason">Reason (saved in history)</label>
        <textarea id="action-reason" className="form-textarea" rows={2} value={form.reason} onChange={set('reason')} maxLength={500} />
      </div>
      {error && <div className="alert alert--error">{error}</div>}
      <div className="btn-group">
        <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={busy}>Back</button>
        <button type="submit" className={`btn ${DESTRUCTIVE.has(action) ? 'btn--danger-solid' : 'btn--primary'}`} disabled={busy}>
          {busy ? 'Saving…' : ACTION_LABELS[action]}
        </button>
      </div>
    </form>
  );
}

function AutopayPanel({ subscriptionId, onChanged }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await platformApi.get(`/subscriptions/${subscriptionId}/autopay`);
      setData(response.data?.data || null);
    } catch (err) {
      setError(platformErrorMessage(err, 'Failed to load autopay'));
    }
  }, [subscriptionId]);

  useEffect(() => { load(); }, [load]);

  const mandate = data?.mandate || null;
  const live = Boolean(mandate?.live);

  const turnOff = async () => {
    const ok = await confirm({
      title: 'Turn off autopay',
      message: 'Cancel this autopay at Razorpay? No further charges are taken; the plan runs to its paid-until date. The customer is notified.',
      confirmLabel: 'Turn off autopay',
      variant: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await platformApi.delete(`/subscriptions/${subscriptionId}/autopay`, { data: { reason: reason.trim() || null } });
      toast.success('Autopay turned off');
      setReason('');
      await load();
      onChanged();
    } catch (err) {
      toast.error(platformErrorMessage(err, 'Could not turn off autopay'));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="alert alert--error">{error}</div>;
  if (!data) return <p className="form-hint">Loading autopay…</p>;
  if (!mandate) {
    return (
      <p className="form-hint">
        {data.enabled ? 'Autopay has never been set up for this workspace.' : 'Autopay is switched off on the platform.'}
      </p>
    );
  }

  const status = autopayStatus(mandate.status);
  return (
    <div>
      {!data.enabled && <p className="form-hint">Autopay is switched off on the platform; new setups are refused.</p>}
      <div className="stats-grid" style={{ marginBottom: '0.75rem' }}>
        <div><div className="form-hint">Status</div><span className={`badge badge--${status.badge}`}>{status.label}</span></div>
        <div><div className="form-hint">Amount</div><strong>{formatInr(mandate.amount)} / month</strong></div>
        <div><div className="form-hint">Method</div><strong>{autopayMethodLabel(mandate.method) || '—'}</strong></div>
        <div>
          <div className="form-hint">{live ? 'Next charge' : 'Ended'}</div>
          <strong>{live ? formatDate(mandate.nextChargeAt || mandate.startAt) : formatDate(mandate.endedAt)}</strong>
        </div>
        <div><div className="form-hint">Charges recorded</div><strong>{mandate.chargesRecorded}</strong></div>
        <div><div className="form-hint">Last synced</div><strong>{formatDate(mandate.lastSyncedAt, { withTime: true })}</strong></div>
      </div>
      {!live && mandate.endReason && (
        <p className="form-hint">
          {autopayEndReasonLabel(mandate.endReason)}{mandate.cancelNote ? ` — ${mandate.cancelNote}` : ''}
        </p>
      )}
      {live && mandate.active && !data.covering && (
        <div className="alert alert--error">This mandate no longer matches the plan; it will be stopped at the next sync.</div>
      )}
      <p className="form-hint cell-mono">
        Razorpay {mandate.providerSubscriptionId}{mandate.providerMode ? ` · ${mandate.providerMode}` : ''}
      </p>
      {live && (
        <div className="form-group" style={{ marginTop: '0.75rem' }}>
          <label className="form-label" htmlFor="autopay-reason">Reason for turning off (saved, not shown to the customer)</label>
          <textarea id="autopay-reason" className="form-textarea" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="btn-group" style={{ marginTop: '0.5rem' }}>
            <button type="button" className="btn btn--danger btn--sm" disabled={busy} onClick={turnOff}>
              {busy ? 'Turning off…' : 'Turn off autopay'}
            </button>
          </div>
        </div>
      )}
      {data.history.length > 1 && (
        <div className="table-wrapper" style={{ marginTop: '0.75rem' }}>
          <table className="data-table">
            <thead>
              <tr><th>Set up</th><th>Status</th><th>Amount</th><th>Charges</th><th>Ended</th></tr>
            </thead>
            <tbody>
              {data.history.map((row) => {
                const rowStatus = autopayStatus(row.status);
                return (
                  <tr key={row.id}>
                    <td>{formatDate(row.createdAt, { withTime: true })}</td>
                    <td><span className={`badge badge--${rowStatus.badge}`}>{rowStatus.label}</span></td>
                    <td>{formatInr(row.amount)}</td>
                    <td>{row.chargesRecorded}</td>
                    <td>{row.endedAt ? `${formatDate(row.endedAt)} · ${autopayEndReasonLabel(row.endReason) || '—'}` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SubscriptionDetail({ subscriptionId, plans, onClose, onChanged }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [subscription, setSubscription] = useState(null);
  const [history, setHistory] = useState([]);
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [detail, events] = await Promise.all([
        platformApi.get(`/subscriptions/${subscriptionId}`),
        platformApi.get('/subscription/history', { params: { subscriptionId, limit: 50 } }),
      ]);
      setSubscription(detail.data?.data || null);
      setHistory(events.data?.data || []);
    } catch (error) {
      setLoadError(platformErrorMessage(error, 'Failed to load subscription'));
    }
  }, [subscriptionId]);

  useEffect(() => { load(); }, [load]);

  const planName = useMemo(() => {
    const byId = new Map(plans.map((p) => [p.id, p.name]));
    return (id) => (id ? byId.get(String(id)) || 'Unknown plan' : null);
  }, [plans]);

  const runAction = async (request) => {
    const ok = await confirm({
      title: ACTION_LABELS[action],
      message: `Apply "${ACTION_LABELS[action]}" to ${tenantLabel(subscription)}? The tenant sees the change immediately.`,
      confirmLabel: 'Apply',
      variant: DESTRUCTIVE.has(action) ? 'danger' : 'info',
    });
    if (!ok) return;
    setBusy(true);
    try {
      if (request.method === 'patch') {
        await platformApi.patch(request.path, request.body);
      } else {
        await platformApi.post(request.path, request.body);
      }
      toast.success('Subscription updated');
      setAction(null);
      await load();
      onChanged();
    } catch (error) {
      toast.error(platformErrorMessage(error, 'Action failed'));
    } finally {
      setBusy(false);
    }
  };

  const actions = availableActions(subscription);

  return (
    <Modal title={subscription ? tenantLabel(subscription) : 'Subscription'} subtitle={subscription?.tenant?.subdomain || subscription?.tenantId} onClose={onClose} size="lg">
      {loadError && <div className="alert alert--error">{loadError}</div>}
      {!subscription && !loadError && <p>Loading…</p>}
      {subscription && (
        <div>
          <div className="stats-grid" style={{ marginBottom: '1rem' }}>
            <div><div className="form-hint">Plan</div><strong>{subscription.plan?.name || '—'}</strong></div>
            <div><div className="form-hint">Status</div><StatusBadge status={subscriptionStatusLabel(subscription)} /></div>
            <div><div className="form-hint">Cycle</div><strong>{subscription.billingCycle || '—'}</strong></div>
            <div><div className="form-hint">Period</div><strong>{periodLabel(subscription)}</strong></div>
            <div><div className="form-hint">Payer</div><strong>{payerLabel(subscription.payerType)}</strong></div>
            {subscription.graceEndsAt && (
              <div><div className="form-hint">Grace ends</div><strong>{formatDate(subscription.graceEndsAt)}</strong></div>
            )}
          </div>
          <p className="form-hint cell-mono">Subscription {subscription.id} · Organization {subscription.organizationId}</p>
          {subscription.cancelAtPeriodEnd && (
            <div className="alert alert--error">Cancels at period end ({formatDate(subscription.subscriptionEnd)}).</div>
          )}

          {!action && actions.length > 0 && (
            <div className="btn-group" style={{ flexWrap: 'wrap', marginTop: '0.5rem' }}>
              {actions.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`btn btn--sm ${DESTRUCTIVE.has(key) ? 'btn--danger' : 'btn--secondary'}`}
                  onClick={() => setAction(key)}
                >
                  {ACTION_LABELS[key]}
                </button>
              ))}
            </div>
          )}
          {action && (
            <ActionForm
              key={action}
              action={action}
              subscription={subscription}
              plans={plans}
              busy={busy}
              onSubmit={runAction}
              onCancel={() => setAction(null)}
            />
          )}

          <h3 style={{ marginTop: '1.5rem' }}>Autopay</h3>
          <AutopayPanel subscriptionId={subscription.id} onChanged={onChanged} />

          <h3 style={{ marginTop: '1.5rem' }}>History</h3>
          {history.length === 0 ? (
            <p className="form-hint">No lifecycle events yet.</p>
          ) : (
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr><th>When</th><th>Change</th><th>Event</th><th>By</th><th>Reason</th></tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDate(row.createdAt, { withTime: true })}</td>
                      <td>
                        {row.previousState} → {row.currentState}
                        {row.metadata?.previousPlanId && row.metadata?.planId && (
                          <div className="form-hint">{planName(row.metadata.previousPlanId)} → {planName(row.metadata.planId)}</div>
                        )}
                      </td>
                      <td className="cell-mono">{row.trigger}</td>
                      <td>{row.source || '—'}</td>
                      <td>{row.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function Subscriptions() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0 });
  const [plans, setPlans] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: '', planId: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async (page = 1, current = filters) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 20 };
      if (current.search.trim()) params.search = current.search.trim();
      if (current.status) params.status = current.status;
      if (current.planId) params.planId = current.planId;
      const response = await platformApi.get('/subscriptions', { params });
      setItems(response.data?.data || []);
      setMeta(response.data?.meta || { page, limit: 20, total: 0 });
    } catch (err) {
      const message = platformErrorMessage(err, 'Failed to load subscriptions');
      setError(message);
      toast.error(message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    fetchList(1);
    platformApi.get('/plans', { params: { limit: 100 } })
      .then((res) => setPlans(res.data?.data || []))
      .catch(() => setPlans([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil((meta.total || 0) / (meta.limit || 20)));

  if (loading && items.length === 0 && !error) {
    return <LoadingScreen message="Loading subscriptions..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="Subscriptions"
        description="Every workspace's plan and status. Change plans, extend trials, cancel, suspend or reactivate. Each change is recorded with a reason."
      />

      <form
        className="card card__body"
        style={{ marginBottom: '1rem' }}
        onSubmit={(e) => { e.preventDefault(); fetchList(1); }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="sub-search">Search</label>
            <input
              id="sub-search"
              className="form-input"
              placeholder="Workspace name, subdomain or tenant id"
              value={filters.search}
              onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="sub-status">Status</label>
            <select id="sub-status" className="form-select" value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
              <option value="">All</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="sub-plan">Plan</label>
            <select id="sub-plan" className="form-select" value={filters.planId} onChange={(e) => setFilters((p) => ({ ...p, planId: e.target.value }))}>
              <option value="">All</option>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0, display: 'flex', alignItems: 'flex-end' }}>
            <button type="submit" className="btn btn--primary" disabled={loading}>{loading ? 'Loading…' : 'Apply'}</button>
          </div>
        </div>
      </form>

      {error && <div className="alert alert--error">{error}</div>}

      {items.length === 0 ? (
        <div className="card">
          <EmptyState title="No subscriptions found" description="Try a different search or filter." />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr>
                  <th>Workspace</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Period</th>
                  <th>Payer</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((sub) => (
                  <tr key={sub.id}>
                    <td data-label="Workspace">
                      <strong>{tenantLabel(sub)}</strong>
                      {sub.tenant?.subdomain && sub.tenant?.name && <div className="form-hint">{sub.tenant.subdomain}</div>}
                    </td>
                    <td data-label="Plan">
                      {sub.plan?.name || '—'}
                      <div className="form-hint">{planPrice(sub.plan, sub.billingCycle)}</div>
                    </td>
                    <td data-label="Status"><StatusBadge status={subscriptionStatusLabel(sub)} /></td>
                    <td data-label="Period">
                      {periodLabel(sub)}
                      {sub.autopay && (
                        <div className="form-hint">
                          {sub.autopay.active
                            ? `Autopay ${sub.autopay.status === 'pending' ? 'retrying' : 'on'}${sub.autopay.method ? ` · ${autopayMethodLabel(sub.autopay.method)}` : ''}`
                            : 'Autopay awaiting approval'}
                        </div>
                      )}
                    </td>
                    <td data-label="Payer">{payerLabel(sub.payerType)}</td>
                    <td data-label="Actions" className="cell-actions">
                      <button type="button" className="btn btn--secondary btn--sm" onClick={() => setSelectedId(sub.id)}>
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="btn-group" style={{ padding: '1rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn--secondary btn--sm" disabled={meta.page <= 1 || loading} onClick={() => fetchList(meta.page - 1)}>Previous</button>
              <span className="form-hint">Page {meta.page} of {totalPages} ({meta.total} total)</span>
              <button type="button" className="btn btn--secondary btn--sm" disabled={meta.page >= totalPages || loading} onClick={() => fetchList(meta.page + 1)}>Next</button>
            </div>
          )}
        </div>
      )}

      {selectedId && (
        <SubscriptionDetail
          subscriptionId={selectedId}
          plans={plans}
          onClose={() => setSelectedId(null)}
          onChanged={() => fetchList(meta.page)}
        />
      )}
    </div>
  );
}

export default Subscriptions;
